import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import {
  ConfirmDialog,
  DataTable,
  DateField,
  EmptyState,
  Notice,
  PageHeader,
  StatCard,
  StatusChip,
  useNotify,
  type DataTableColumn,
  type StatusTone,
} from "@routes-plan/ui";
import HeaderResponsive from "../../../components/layouts/HeaderResponsive";
import { useSelection } from "../../../components/context/SelectionContext";
import { Office } from "../../../models/office";
import { Route } from "../../../models/route";
import { nowOperationalDateString } from "../../../utils/operationalDateTime";
import PermissionButton from "../../../components/common/PermissionButton";
import { useResourcePermissions } from "../../../hooks/useResourcePermissions";
import { useT, TextKey } from "../../../i18n";
import { setPageTitle } from "../../../utils/pageTitle";
import type { RouteMapData } from "./routeMapTypes";
import { formatCurrency } from "../../../utils/format/currency";
import { FilterBar, OfficeRouteFields } from "../components/ProcessFilters";

// MapLibre pesa: solo se carga al abrir la pestaña Mapa.
const RouteDayMap = lazy(() => import("./RouteDayMap"));

type TFn = (key: TextKey, vars?: Record<string, string | number>) => string;

/** Mostrar flags de pendiente / no visita en tabla y resumen. */
const SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO = true;

interface ShowAlerts {
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface RouteDailyMeta {
  id: number;
  date: string;
  state: number;
  stateLabel: string;
  loanCount: number;
  routeId: number;
  routeName: string;
  userId: number | null;
  username: string | null;
}

interface RouteDailyRow {
  visitOrderId: number;
  loanId: number;
  position: number;
  idNumber: string;
  fullName: string;
  balance: number;
  loanState?: number;
  loanTypeName?: string;
  loanTypeKindName?: string;
  sync: boolean;
  pending: boolean;
  noVisit: boolean;
  paid: boolean;
}

interface Summary {
  total: number;
  synced: number;
  notSynced: number;
  pending: number;
  noVisit: number;
  paid: number;
}

/** Texto de plazo según type_constant (BD suele guardar Diaria, Semanal, …). */
function plazoTipoLabel(raw: string | undefined | null, tr: TFn): string {
  const t = String(raw ?? "").trim();
  if (!t) return "—";
  const lower = t.toLowerCase();
  if (lower === "diaria" || lower === "diario") return tr("routeTracking.termDaily");
  if (lower === "semanal") return tr("routeTracking.termWeekly");
  if (lower === "quincenal") return tr("routeTracking.termBiweekly");
  if (lower === "mensual") return tr("routeTracking.termMonthly");
  return t;
}

/** Sí/No como chip: en color solo cuando es "Sí". */
const flagChip = (value: boolean, tone: StatusTone, tr: TFn) => (
  <StatusChip label={value ? tr("common.yes") : tr("common.no")} tone={value ? tone : "neutral"} dot={false} />
);

const RouteTracking: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const notify = useNotify();
  const showAlert = useCallback(
    (message: string, severity: "success" | "info" | "warning" | "error" = "info") => notify[severity](message),
    [notify]
  );
  useEffect(() => {
    setPageTitle("routeTracking.title");
  }, []);
  const {
    office_id: officeId,
    collect_id: routeId,
    setOffice_id: setOfficeId,
    setCollect_id: setRouteId,
  } = useSelection();
  const { canRead, canUpdate } = useResourcePermissions('/processes/route-tracking');
  // El mapa muestra dónde se registró cada abono: pide «Mostrar ubicación» de Abonos y préstamos.
  const { canShowLocation } = useResourcePermissions('/loans/payments');
  const [view, setView] = useState<"table" | "map">("table");
  const [mapData, setMapData] = useState<RouteMapData | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapMessage, setMapMessage] = useState<string | null>(null);
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [dateStr, setDateStr] = useState(() => nowOperationalDateString());
  const [routeMeta, setRouteMeta] = useState<RouteDailyMeta | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<RouteDailyRow[]>([]);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [confirmGenerate, setConfirmGenerate] = useState(false);

  useEffect(() => {
    const loadOffices = async () => {
      try {
        const { data } = await axios.get("offices");
        const list = Array.isArray(data?.data) ? data.data : [];
        setOffices(list);
      } catch (e) {
        console.error(e);
      }
    };
    void loadOffices();
  }, []);

  useEffect(() => {
    if (offices.length === 0) return;
    setOfficeId((prev) =>
      prev && !offices.some((o) => String(o.id) === String(prev)) ? "" : prev
    );
  }, [offices, setOfficeId]);

  const clearRouteTrackingTable = useCallback(() => {
    setRouteMeta(null);
    setSummary(null);
    setRows([]);
    setInfoMessage(null);
    setLoadedOnce(false);
  }, []);

  useEffect(() => {
    clearRouteTrackingTable();
  }, [officeId, clearRouteTrackingTable]);

  useEffect(() => {
    clearRouteTrackingTable();
  }, [routeId, clearRouteTrackingTable]);

  useEffect(() => {
    if (!officeId) {
      setOfficeRoutes([]);
      return;
    }
    let cancelled = false;
    const loadOfficeRoutes = async () => {
      try {
        const { data } = await axios.get(`offices/${officeId}/routes`);
        const list = Array.isArray(data) ? data : [];
        if (cancelled) return;
        setOfficeRoutes(list);
        setRouteId((prev) =>
          prev && !list.some((c) => String(c.id) === String(prev)) ? "" : prev
        );
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setOfficeRoutes([]);
        }
      }
    };
    void loadOfficeRoutes();
    return () => {
      cancelled = true;
    };
  }, [officeId, setRouteId]);

  const fetchTracking = useCallback(async () => {
    if (!canRead) {
      showAlert(t("routeTracking.noPermissionQuery"), "warning");
      return;
    }
    if (!routeId) {
      showAlert(t("routeTracking.selectRouteWarning"), "warning");
      return;
    }
    showLoading();
    setInfoMessage(null);
    try {
      const { data } = await axios.get<{
        data: { visitOrder: RouteDailyMeta; rows: RouteDailyRow[]; summary: Summary } | null;
        message?: string;
      }>("visit-orders/daily-tracking", {
        params: { routeId: Number(routeId), date: dateStr },
      });
      if (!data?.data) {
        setRouteMeta(null);
        setSummary(null);
        setRows([]);
        setInfoMessage(t("routeTracking.noRouteData"));
        showAlert(t("routeTracking.noRouteForDate"), "info");
        setLoadedOnce(true);
        return;
      }
      setRouteMeta(data.data.visitOrder);
      setSummary(data.data.summary);
      setRows(data.data.rows);
      setInfoMessage(null);
      setLoadedOnce(true);
    } catch (err) {
      console.error(err);
      setRouteMeta(null);
      setSummary(null);
      setRows([]);
      showAlert(t("routeTracking.loadError"), "error");
    } finally {
      hideLoading();
    }
  }, [canRead, routeId, dateStr, hideLoading, showAlert, showLoading, t]);

  const fetchMap = useCallback(async () => {
    if (!routeId) return;
    setMapLoading(true);
    setMapMessage(null);
    try {
      const { data } = await axios.get<{ data: RouteMapData | null; message?: string }>("visit-orders/daily-map", {
        params: { routeId: Number(routeId), date: dateStr },
      });
      setMapData(data?.data ?? null);
      if (!data?.data) setMapMessage(t("routeTracking.noRouteData"));
    } catch (err: any) {
      setMapData(null);
      setMapMessage(
        err?.response?.data?.code === "LOCATION_PERMISSION" ? t("routeTracking.map.noPermission") : t("routeTracking.map.dataError")
      );
    } finally {
      setMapLoading(false);
    }
  }, [routeId, dateStr, t]);

  // Al consultar con la pestaña Mapa abierta, o al abrirla, se cargan los datos del mapa.
  useEffect(() => {
    if (view === "map" && loadedOnce && canShowLocation) void fetchMap();
  }, [view, loadedOnce, canShowLocation, fetchMap, routeMeta]);

  const todayOperational = nowOperationalDateString();
  const canGenerateRouteToday = Boolean(routeId) && dateStr === todayOperational;

  const generateRouteDay = useCallback(async () => {
    if (!canUpdate) {
      showAlert(t("routeTracking.noPermissionUpdate"), "warning");
      return;
    }
    if (!routeId) {
      showAlert(t("routeTracking.selectRouteWarning"), "warning");
      return;
    }
    if (dateStr !== nowOperationalDateString()) {
      showAlert(t("routeTracking.onlyToday"), "warning");
      return;
    }
    setConfirmGenerate(true);
  }, [canUpdate, routeId, dateStr, showAlert, t]);

  const runGenerateRouteDay = useCallback(async () => {
    setConfirmGenerate(false);
    showLoading();
    try {
      const { data } = await axios.post<{ message?: string }>("visit-orders/generate-day", {
        routeId: Number(routeId),
        date: dateStr,
      });
      showAlert(t("routeTracking.generatedOk"), "success");
      await fetchTracking();
    } catch (err) {
      console.error(err);
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t("routeTracking.generateError");
      showAlert(msg, "error");
    } finally {
      hideLoading();
    }
  }, [routeId, dateStr, fetchTracking, hideLoading, showAlert, showLoading, t]);

  const columns: DataTableColumn<RouteDailyRow>[] = useMemo(() => {
    const cols: DataTableColumn<RouteDailyRow>[] = [
      { id: "fullName", header: t("routeTracking.colClient"), value: (r) => r.fullName, sortable: true },
      { id: "position", header: t("routeTracking.colPosition"), value: (r) => r.position, sortable: true, align: "center", width: 64 },
      { id: "idNumber", header: t("routeTracking.colIdNumber"), value: (r) => r.idNumber, sortable: true, hideBelow: "sm" },
      {
        id: "balance",
        header: t("routeTracking.colBalance"),
        value: (r) => Number(r.balance ?? 0),
        render: (r) => formatCurrency(Number(r.balance ?? 0)),
        sortable: true,
        align: "right",
      },
      {
        id: "loanState",
        header: t("routeTracking.colLoan"),
        value: (r) => (Number(r.loanState) === 1 ? t("routeTracking.loanCancelled") : t("routeTracking.loanActive")),
        render: (r) => (
          <StatusChip
            label={Number(r.loanState) === 1 ? t("routeTracking.loanCancelled") : t("routeTracking.loanActive")}
            tone={Number(r.loanState) === 1 ? "neutral" : "info"}
          />
        ),
        hideBelow: "md",
      },
      { id: "type", header: t("routeTracking.colType"), value: (r) => plazoTipoLabel(r.loanTypeKindName, t), sortable: true, hideBelow: "md" },
      { id: "sync", header: t("routeTracking.colSync"), value: (r) => (r.sync ? t("common.yes") : t("common.no")), render: (r) => flagChip(r.sync, "success", t), align: "center" },
    ];
    if (SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO) {
      cols.push(
        { id: "pending", header: t("routeTracking.colPending"), value: (r) => (r.pending ? t("common.yes") : t("common.no")), render: (r) => flagChip(r.pending, "warning", t), align: "center" },
        { id: "noVisit", header: t("routeTracking.colNoVisit"), value: (r) => (r.noVisit ? t("common.yes") : t("common.no")), render: (r) => flagChip(r.noVisit, "danger", t), align: "center" }
      );
    }
    cols.push({ id: "paid", header: t("routeTracking.colPaid"), value: (r) => (r.paid ? t("common.yes") : t("common.no")), render: (r) => flagChip(r.paid, "info", t), align: "center" });
    return cols;
  }, [t]);

  const stats = summary
    ? [
        { label: t("routeTracking.statTotal"), value: summary.total },
        { label: t("routeTracking.statSynced"), value: summary.synced, hint: t("routeTracking.notSynced", { count: summary.notSynced }) },
        ...(SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO
          ? [
              { label: t("routeTracking.statPending"), value: summary.pending },
              { label: t("routeTracking.statNoVisit"), value: summary.noVisit },
            ]
          : []),
        { label: t("routeTracking.statPaid"), value: summary.paid },
      ]
    : [];

  return (
    <HeaderResponsive>
      <PageHeader
        title={t("routeTracking.title")}
        description={t("routeTracking.subtitle")}
        actions={
          <PermissionButton
            allowed={canUpdate}
            deniedTooltip={t("routeTracking.noPermissionUpdateShort")}
            tooltip={canGenerateRouteToday ? t("routeTracking.updateRoute") : t("routeTracking.onlyTodayHint")}
            variant="outlined"
            startIcon={<AutorenewOutlinedIcon />}
            disabled={!canGenerateRouteToday}
            onClick={() => void generateRouteDay()}
          >
            {t("routeTracking.updateRoute")}
          </PermissionButton>
        }
      />

      <Box sx={{ mb: 2 }}>
        <Notice severity="info" dismissible>
          {t("routeTracking.intro")}
          {SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO ? t("routeTracking.introPendingNoVisit") : ""}
        </Notice>
      </Box>

      <FilterBar>
        <OfficeRouteFields
          idPrefix="route-tracking"
          offices={offices}
          routes={officeRoutes}
          officeId={officeId}
          routeId={routeId}
          onOfficeChange={(v) => setOfficeId(v)}
          onRouteChange={(v) => setRouteId(v)}
        />
        <DateField
          id="route-tracking-date"
          label={t("routeTracking.routeDate")}
          value={dateStr}
          onChange={(v) => setDateStr(v ?? nowOperationalDateString())}
          fullWidth
          data-tour="route-date"
        />
        <Box sx={{ display: "flex", alignItems: "center", minHeight: 56, "& > span, & button": { width: "100%" } }}>
          <PermissionButton
            allowed={canRead}
            deniedTooltip={t("routeTracking.noPermissionQueryShort")}
            fullWidth
            size="large"
            variant="contained"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => void fetchTracking()}
            disabled={!routeId}
            spanSx={{ width: "100%" }}
          >
            {t("routeTracking.query")}
          </PermissionButton>
        </Box>
      </FilterBar>

      {routeMeta && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }} aria-label={t("routeTracking.metaLabel")}>
          <StatusChip label={t("routeTracking.routeNumber", { id: routeMeta.id })} tone="neutral" dot={false} />
          <StatusChip label={routeMeta.stateLabel} tone="info" />
          <StatusChip label={t("routeTracking.registered", { count: routeMeta.loanCount ?? summary?.total ?? 0 })} tone="neutral" dot={false} />
          {routeMeta.username && <StatusChip label={t("routeTracking.user", { username: routeMeta.username })} tone="neutral" dot={false} />}
        </Stack>
      )}

      {summary && (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            mb: 2,
            gridTemplateColumns: "repeat(auto-fit, minmax(min(130px, 100%), 1fr))",
          }}
        >
          {stats.map((st) => (
            <StatCard key={st.label} label={st.label} value={st.value} hint={"hint" in st ? st.hint : undefined} />
          ))}
        </Box>
      )}

      {infoMessage && (
        <Box sx={{ mb: 2 }}>
          <Notice severity="info">{infoMessage}</Notice>
        </Box>
      )}

      <Paper variant="outlined" sx={{ minWidth: 0 }}>
        {canShowLocation && loadedOnce && routeMeta && (
          <Tabs
            value={view}
            onChange={(_, v) => setView(v)}
            sx={{ px: 1, borderBottom: 1, borderColor: "divider" }}
            aria-label={t("routeTracking.viewLabel")}
            data-tour="route-view"
          >
            <Tab value="table" label={t("routeTracking.tabTable")} />
            <Tab value="map" label={t("routeTracking.tabMap")} />
          </Tabs>
        )}
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          {view === "map" && canShowLocation && routeMeta ? (
            mapLoading && !mapData ? (
              <Skeleton variant="rounded" height={420} />
            ) : mapData ? (
              <Suspense fallback={<Skeleton variant="rounded" height={420} />}>
                <RouteDayMap data={mapData} />
              </Suspense>
            ) : (
              <Notice severity="info">{mapMessage ?? t("routeTracking.noRouteData")}</Notice>
            )
          ) : (
            <DataTable<RouteDailyRow>
              rows={rows}
              columns={columns}
              getRowId={(r) => r.loanId}
              searchable
              searchPlaceholder={t("routeTracking.filterPlaceholder")}
              exportFileName={t("routeTracking.exportFileName", { date: dateStr })}
              initialSort={{ columnId: "position", direction: "asc" }}
              initialPageSize={50}
              ariaLabel={t("routeTracking.title")}
              emptyState={
                <EmptyState
                  dense
                  icon={<RouteOutlinedIcon />}
                  title={loadedOnce ? t("routeTracking.noLoansForDate") : t("routeTracking.selectToStartTitle")}
                  description={loadedOnce ? undefined : t("routeTracking.selectToStart")}
                />
              }
            />
          )}
        </Box>
      </Paper>

      <ConfirmDialog
        open={confirmGenerate}
        variant="warning"
        title={t("routeTracking.confirmGenerateTitle")}
        message={
          SHOW_PENDING_NOVISIT_IN_SEGUIMIENTO ? t("routeTracking.confirmGenerateWithFlags") : t("routeTracking.confirmGenerate")
        }
        confirmLabel={t("routeTracking.updateRoute")}
        cancelLabel={t("common.cancel")}
        onConfirm={() => void runGenerateRouteDay()}
        onCancel={() => setConfirmGenerate(false)}
      />
    </HeaderResponsive>
  );
};

export default RouteTracking;
